<template>
  <AppDialogContent
    v-model="dialogValue"
    :disabled-submit="submitDisabled"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <h1 class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </h1>
        </div>
        <div v-else style="align-self: center">
          <h1 class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.selectHorseForSlot.heading",
                "Select horse for slot"
              )
            }}
          </h1>
          <p>
            {{
              $t(
                "events:entryAction.selectHorseForSlot.explanation.text",
                "You are going to substitute horse slot with a horse."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseTexts.reason">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t(
                "events:entryAction.selectHorseForSlot.competitionCode.label",
                "Entry"
              )
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t(
                "events:entryAction.selectHorseForSlot.athleteNames.label",
                "Athlete"
              )
            }}
          </template>
          <template #horseNamesHeader>
            {{
              $t(
                "events:entryAction.selectHorseForSlot.horseNames.label",
                "Horse"
              )
            }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <div>
          <h3>
            {{
              $t(
                "events:entryAction.selectHorseForSlot.selectHorse.heading",
                "Select a new horse to substitute a slot with…"
              )
            }}
          </h3>
          <p class="text-muted">
            {{
              $t(
                "events:entryAction.selectHorseForSlot.selectHorse.hint",
                "Only horses with FEI ID, registered in FIPA, will appear in search. If you cannot find a horse, initiate adding a horse from FEI Entry System or contact your Federation."
              )
            }}
          </p>

          <AppSelectHorse
            v-model="horse"
            :input-props="{
              returnObject: true,
              label: $t(
                'events:entryAction.selectHorseForSlot.selectHorse.label',
                'Select horse'
              ),
              hint: $t(
                'events:entryAction.selectHorseForSlot.selectHorse.hint',
                'Start typing a horse name or FEI ID.'
              )
            }"
            :query-variables="{ hasActiveIdentifiers: true }"
            :horse-fragment="horseFragment"
            :external-loading="loadingRecentHorses"
            :default-horses="competitorRecentHorses"
          />

          <template v-if="horse">
            <br />
            <template v-if="masterConfiguration.collectEntryBillingContact">
              <AppBillingContact
                contactable-type="Entry"
                :contact-object="entry.billingContact"
                :contact-id="
                  entry.billingContact ? entry.billingContact.id : undefined
                "
                :person-id="entry.registration.registrantPersonId"
                :contactable-id="entry.id"
                :contact-type="
                  entry.billingContact
                    ? entry.billingContact.contactType
                    : undefined
                "
                :force-cols="12"
                @set-contact="
                  billingContact => (billingContactForm = billingContact)
                "
                @edit-mode-state="
                  editModeState => (billingContactEditFormState = editModeState)
                "
              />
              <br />
            </template>
            <h3>
              {{
                $t(
                  "events:entryAction.selectHorseForSlot.outcome.heading",
                  "Outcome"
                )
              }}
            </h3>

            <div class="d-flex py-3">
              <AppHorseSlot badge class="mr-2" />
              {{
                $t(
                  "events:entryAction.selectHorseForSlot.outcome.text",
                  "will be substituted by: "
                )
              }}
              {{ horse.name }} {{ horse.feiid }}
            </div>
          </template>
        </div>

        <br />

        <div class="mb-2">
          <b>{{
            $t(
              "events:entryAction.selectHorseForSlot.alertNote.heading",
              "Please note:"
            )
          }}</b>
        </div>

        <AppAlertNotice center>
          <div
            v-html="
              $t(
                'events:entryAction.selectHorseForSlot.alertNote.text',
                'Horse slot will be substituted with a selected horse. You cannot reverse this action.'
              )
            "
          />
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryAction.selectHorseForSlot.confirmEnterHorse.button",
          "Enter selected horse"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useEntryFeiActionsInjections } from "./compositions";
import { getFederationConfiguration } from "@composables/masterdata/config";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";

import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";

import AppSelectHorse from "@AppSelects/AppSelectHorse.vue";
import AppHorseSlot from "@elements/AppHorseSlot.vue";

import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import AppBillingContact, {
  prepContactForBillingInput
} from "@elements/billing/AppBillingContact.vue";

import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";
import { useQuery } from "@vue/apollo-composable";

defineOptions({
  name: "EntryFeiHorseSelect"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();
const { masterConfiguration } = getFederationConfiguration();

const modal = ref(false);
const horse = ref(null);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);
const competitorRecentHorses = ref([]);
const billingContactForm = ref(null);
const billingContactEditFormState = ref(false);
const entryLoading = ref(false);
const entry = ref(null);

const horseFragment = computed(() => {
  return gql`
    fragment _ on Horse {
      feiid
    }
  `;
});

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

const submitDisabled = computed(() => {
  return !horse.value || billingContactEditFormState.value == true;
});

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
      horse.value = null;
      billingContactForm.value = null;
      billingContactEditFormState.value = false;
    }
    modal.value = v;
  }
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.selectHorseForSlot.success.heading",
          "Enter horse for a slot"
        ),
        main: t(
          "events:entryAction.selectHorseForSlot.success.mainMessage.text",
          "Horse entered"
        ),
        additional: t(
          "events:entryAction.selectHorseForSlot.success.additionalMessage.text",
          "Check if you have more slots to fill."
        ),
        okBtn: t(
          "events:entryAction.selectHorseForSlot.success.ok.button",
          "OK"
        )
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.selectHorseForSlot.failed.heading",
          "Enter horse for a slot"
        ),
        main: t(
          "events:entryAction.selectHorseForSlot.failed.mainMessage.text",
          "Action failed"
        ),
        reason: t(
          "events:entryAction.selectHorseForSlot.failed.reason.text",
          "The FEI entry system reported the following reason:"
        ),
        additional: t(
          "events:entryAction.selectHorseForSlot.failed.additionalMessage.text",
          "Please contact your National Federation, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.selectHorseForSlot.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

const { onResult, loading: loadingRecentHorses } = useQuery(
  gql`
    query competitorRecentHorses($id: ID!) {
      person(id: $id) {
        id
        competitorRecentHorses {
          id
          name
          feiid
          ${AppEntryRegistrationOverview.fragments.horseFields}
          ${AppShowHorseName.fragments.horse}
          ${AppSelectHorse.fragments.horseFields}
        }
      }
    }
  `,
  () => ({
    id: entry.value.registration.athletes[0].id
  }),
  () => ({
    enabled: dialogValue.value === true && !entryLoading.value,
    fetchPolicy: "cache-first"
  })
);

onResult(({ loading, data }) => {
  if (loading) return;
  competitorRecentHorses.value = data.person?.competitorRecentHorses ?? [];
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query EntryFeiHorseSelectFetch($id: ID!) {
          entry(id: $id) {
            id
            billingContact {
              id
              contactType
            }
            allowedMutations
            registration {
              id
              vaultingLungerPersonId
              vaultingReserveLungerPersonId
              registrationGrooms {
                id
                personId
              }
              eventId
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
              athletes {
                id
                name
                ${AppEntryRegistrationOverview.fragments.personFields}
              }
              horses {
                id
                name
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
            }
            competition {
              id
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  if (!horse.value) return;
  formSubmitted.value = true;

  let localFragments = fragments;
  if (!localFragments) {
    localFragments = {
      horseFragment: () => null,
      billingContactFragment: () => null,
      registrationHorseFragment: () => null
    };
  }

  let contact = null;
  if (masterConfiguration.value.collectEntryBillingContact) {
    contact = billingContactForm.value
      ? prepContactForBillingInput(billingContactForm.value)
      : null;
  }

  try {
    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entrySpecifyHorseForSlot(
          $billingContact: ContactForBillingInput
          $entryId: ID
          $horseId: ID
        ) {
          entrySpecifyHorseForSlot(
            input: { 
              billingContact: $billingContact
              entryId: $entryId
              horseId: $horseId 
            }
          ) {
            entry {
              id
              status
              resolution
              allowedMutations
              connectedWithFei
              billingContact {
                id
                ${
                  localFragments.billingContactFragment
                    ? localFragments.billingContactFragment()
                    : ""
                }
              }
              registration {
                id
                status
                horseIds
                horses {
                  id
                  ${localFragments.horseFragment ? localFragments.horseFragment() : ""}
                }
                registrationHorses {
                  id
                  horseId
                  ${
                    localFragments.registrationHorseFragment
                      ? localFragments.registrationHorseFragment()
                      : ""
                  }
                }
                allowedMutations
              }
            }
          }
        }
      `,
      variables: {
        billingContact: contact,
        entryId: entry.value.id,
        horseId: horse.value.id
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      responseType.value = "success";
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
